import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Fab,
  Fade,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BaseDialog from './BaseDialog';
import TypewriterText from './TypewriterText';
import promptChatService from '../../services/promptChatService';

// Composant pour interpréter le markdown simple
const MarkdownText = ({ children }) => {
  const parseMarkdown = (text) => {
    if (!text) return text;
    
    // Diviser en lignes pour traiter les headers et listes
    const lines = text.split('\n');
    const elements = [];
    
    lines.forEach((line, index) => {
      let processedLine = line;
      
      // Headers (### ## #)
      if (line.startsWith('### ')) {
        elements.push(
          <Typography key={index} variant="h6" sx={{ fontWeight: 600, color: '#3F51B5', mt: 2, mb: 1 }}>
            {line.replace('### ', '')}
          </Typography>
        );
        return;
      } else if (line.startsWith('## ')) {
        elements.push(
          <Typography key={index} variant="h5" sx={{ fontWeight: 700, color: '#3F51B5', mt: 2, mb: 1 }}>
            {line.replace('## ', '')}
          </Typography>
        );
        return;
      } else if (line.startsWith('# ')) {
        elements.push(
          <Typography key={index} variant="h4" sx={{ fontWeight: 700, color: '#3F51B5', mt: 2, mb: 1 }}>
            {line.replace('# ', '')}
          </Typography>
        );
        return;
      }
      
      // Listes (- ou *)
      if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 2 }}>
            <Box sx={{ minWidth: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3F51B5', mt: 1, mr: 1.5 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {processTextFormatting(line.replace(/^[*-] /, ''))}
            </Typography>
          </Box>
        );
        return;
      }
      
      // Ligne vide
      if (line.trim() === '') {
        elements.push(<Box key={index} sx={{ height: '8px' }} />);
        return;
      }
      
      // Texte normal
      elements.push(
        <Typography key={index} variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
          {processTextFormatting(processedLine)}
        </Typography>
      );
    });
    
    return elements;
  };
  
  // Fonction pour traiter le formatage (gras, italique, code)
  const processTextFormatting = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Regex combinée pour gras, code inline
    const formatRegex = /(\*\*(.*?)\*\*|`([^`]+)`)/g;
    let match;
    
    while ((match = formatRegex.exec(text)) !== null) {
      // Ajouter le texte avant
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }
      
      if (match[0].startsWith('**')) {
        // Texte en gras
        parts.push(<strong key={`bold-${match.index}`} style={{ color: '#3F51B5', fontWeight: 600 }}>{match[2]}</strong>);
      } else if (match[0].startsWith('`')) {
        // Code inline
        parts.push(
          <Box
            key={`code-${match.index}`}
            component="span"
            sx={{
              backgroundColor: 'rgba(63, 81, 181, 0.1)',
              border: '1px solid rgba(63, 81, 181, 0.2)',
              borderRadius: '4px',
              padding: '2px 6px',
              fontFamily: 'monospace',
              fontSize: '0.85em',
              color: '#3F51B5',
              fontWeight: 500
            }}
          >
            {match[3]}
          </Box>
        );
      }
      
      currentIndex = match.index + match[0].length;
    }
    
    // Ajouter le reste du texte
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  return <Box>{parseMarkdown(children)}</Box>;
};

const PromptChatDialog = ({ open, onClose, onPromptGenerated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addSystemNotification } = useNotifications();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [showApplyButton, setShowApplyButton] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [animatedMessages, setAnimatedMessages] = useState(new Set());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialiser la conversation avec le message d'accueil
  useEffect(() => {
    if (open && messages.length === 0) {
      const welcomeMessage = {
        id: 'welcome',
        role: 'assistant',
        content: promptChatService.getWelcomeMessage(),
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [open, messages.length]);

  // Auto-scroll vers le bas
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus sur l'input quand le dialog s'ouvre
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const containsPrompt = (aiResponse) => {
    const promptIndicators = [
      'prompt proposé', 'prompt final', 'voici le prompt', 'prompt pour l\'ia',
      'vous êtes un assistant', 'vous êtes un expert', 'votre rôle est',
      'analysez la transcription', 'générez un résumé', 'créez un compte-rendu'
    ];
    
    const response = aiResponse.toLowerCase();
    return promptIndicators.some(indicator => response.includes(indicator));
  };

  const applyLastAssistantPrompt = () => {
    // Trouver le dernier message de l'assistant
    const lastAssistantMessage = [...messages].reverse().find(msg => msg.role === 'assistant');
    if (lastAssistantMessage) {
      // Essayer d'extraire un prompt structuré, sinon prendre tout le message
      const extractedPrompt = promptChatService.extractPromptFromResponse(lastAssistantMessage.content);
      const promptToApply = extractedPrompt || lastAssistantMessage.content;
      
      onPromptGenerated?.(promptToApply);
      handleClose();
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);
    setShowApplyButton(false); // Masquer le bouton pendant le chargement

    try {
      // Préparer l'historique pour l'API
      const chatHistory = [...messages, userMessage];
      const response = await promptChatService.generatePromptFromChat(chatHistory);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setTypingMessageId(assistantMessage.id); // Marquer ce message pour l'effet de frappe

      // Vérifier si la réponse contient un prompt et afficher le bouton
      if (containsPrompt(response)) {
        setShowApplyButton(true);
      }

      // Tenter d'extraire un prompt si la réponse en contient un
      const extractedPrompt = promptChatService.extractPromptFromResponse(response);
      if (extractedPrompt && extractedPrompt !== response) {
        setGeneratedPrompt(extractedPrompt);
      }

    } catch (error) {
      addSystemNotification('Erreur lors de l\'opération', 'error');
      setError(error.message || 'Erreur lors de la communication avec l\'IA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      addSystemNotification('Erreur lors de la copie:', 'error');
    }
  };

  const handleApplyPrompt = (prompt) => {
    onPromptGenerated?.(prompt);
    onClose();
  };

  const handleClose = () => {
    // Réinitialiser l'état lors de la fermeture
    setMessages([]);
    setInputMessage('');
    setError(null);
    setGeneratedPrompt('');
    setCopied(false);
    setShowApplyButton(false);
    setTypingMessageId(null);
    setAnimatedMessages(new Set()); // Réinitialiser les messages animés
    onClose();
  };

  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    const isWelcome = message.id === 'welcome';

    return (
      <Fade in={true} key={message.id}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            mb: 2,
            px: 1
          }}
        >
          {!isUser && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 1,
                background: isWelcome 
                  ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)'
                  : 'linear-gradient(135deg, #4ECDC4 0%, #6BCF7F 100%)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              <SmartToyIcon sx={{ fontSize: '1rem', color: 'white' }} />
            </Avatar>
          )}
          
          <Paper
            elevation={0}
            sx={{
              maxWidth: '85%',
              p: 2,
              background: isUser
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: isUser ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
              color: isUser ? 'white' : theme.palette.text.primary,
              boxShadow: `0 8px 32px ${isUser ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isUser
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
                pointerEvents: 'none'
              }
            }}
          >
                         <Box
               sx={{
                 position: 'relative',
                 zIndex: 1,
                 lineHeight: 1.5,
                 fontSize: '0.9rem',
                 fontWeight: isUser ? 500 : 400
               }}
             >
               {isUser ? (
                 <Typography
                   variant="body2"
                   sx={{
                     whiteSpace: 'pre-wrap',
                     fontSize: '0.9rem',
                     fontWeight: 500
                   }}
                 >
                   {message.content}
                 </Typography>
                                ) : (isWelcome || message.id === typingMessageId) && !animatedMessages.has(message.id || message.content) ? (
                  <TypewriterText 
                    text={message.content} 
                    speed={isWelcome ? 30 : 20}
                    delay={isWelcome ? 500 : 200}
                    onComplete={() => {
                      // Marquer ce message comme animé
                      setAnimatedMessages(prev => new Set(prev).add(message.id || message.content));
                      
                      if (message.id === typingMessageId) {
                        // Ajouter un délai pour éviter le clignotement
                        setTimeout(() => {
                          setTypingMessageId(null);
                        }, 100);
                      }
                    }}
                  >
                    <MarkdownText />
                  </TypewriterText>
                 ) : (
                  <MarkdownText>{message.content}</MarkdownText>
                 )}
             </Box>

            {/* Bouton de copie pour les réponses de l'assistant */}
            {!isUser && message.content.length > 100 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, position: 'relative', zIndex: 2 }}>
                <Tooltip title="Copier le texte">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyPrompt(message.content)}
                    sx={{
                      background: 'rgba(0,0,0,0.05)',
                      '&:hover': { background: 'rgba(0,0,0,0.1)' },
                      p: 0.5
                    }}
                  >
                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Utiliser comme prompt">
                  <IconButton
                    size="small"
                    onClick={() => handleApplyPrompt(message.content)}
                    sx={{
                      background: 'rgba(63, 81, 181, 0.1)',
                      color: theme.palette.primary.main,
                      '&:hover': { background: 'rgba(63, 81, 181, 0.2)' },
                      p: 0.5
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Paper>

          {isUser && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                ml: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              <PersonIcon sx={{ fontSize: '1rem', color: 'white' }} />
            </Avatar>
          )}
        </Box>
      </Fade>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="🤖 Générateur de prompts IA"
      subtitle="Créez un prompt optimisé avec l'aide de l'intelligence artificielle"
      icon={<SmartToyIcon />}
      maxWidth="md"
      fullWidth
      customPaddingContent={0}
      customBackgroundContent="linear-gradient(135deg, #f8faff 0%, #e8f2ff 100%)"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: isMobile ? '60vh' : '500px' }}>
        {/* Zone de messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1,
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0,0,0,0.05)',
              borderRadius: '3px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(0,0,0,0.3)'
              }
            }
          }}
        >
          {messages.map(renderMessage)}
          
          {/* Indicateur de frappe */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, px: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1,
                  background: 'linear-gradient(135deg, #4ECDC4 0%, #6BCF7F 100%)'
                }}
              >
                <SmartToyIcon sx={{ fontSize: '1rem', color: 'white' }} />
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '18px 18px 18px 6px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                <Typography variant="body2" sx={{ ml: 1, display: 'inline', color: 'text.secondary' }}>
                  L'IA réfléchit...
                </Typography>
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Message d'erreur */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mx: 2, mb: 1 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Zone de saisie */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(0,0,0,0.06)',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={4}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Décrivez vos besoins pour générer un prompt personnalisé..."
              disabled={isLoading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: 'rgba(0,0,0,0.1)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(63, 81, 181, 0.3)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
            <Fab
              color="primary"
              size="small"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              sx={{
                background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
                  transform: 'scale(1.05)'
                },
                '&:disabled': {
                  background: 'rgba(0,0,0,0.12)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <SendIcon />
            </Fab>
          </Box>

          {/* Indication pour l'utilisateur */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: 'text.secondary',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            💡 Entrée pour envoyer • Shift+Entrée pour nouvelle ligne
          </Typography>

          {/* Bouton d'application du prompt */}
          {showApplyButton && (
            <Fade in={showApplyButton}>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={applyLastAssistantPrompt}
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: '25px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)',
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #43A047 0%, #4CAF50 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(76, 175, 80, 0.5)'
                                         },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                     '@keyframes pulse': {
                       '0%': {
                         boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)'
                       },
                       '50%': {
                         boxShadow: '0 8px 32px rgba(76, 175, 80, 0.6)'
                       },
                       '100%': {
                         boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)'
                       }
                     },
                     animation: 'pulse 2s ease-in-out infinite'
                  }}
                >
                  Utiliser ce prompt
                </Button>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default PromptChatDialog; 